import React, { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";

import { credentialsRefreshHasErrored } from "seneca-common/features/user/state";
import { getReferralCodeFromLocation } from "seneca-common/utils/functions/referral";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";
import loaderWithRetry from "seneca-common/utils/loaderWithRetry";
import { analyticsReferredUserLandedOnSite } from "seneca-common/utils/senecaAnalytics";

import { linkToAnonymousCourseDiscoveryPage } from "features/anonymous-pages/course-discovery/links";
import { linkToAnonymousResourcesHubPage } from "features/anonymous-pages/resources-hub/links";
import { linkToAnonymousUnsubscribePage } from "features/anonymous-pages/unsubscribe/links";
import AnonymousUnsubscribePageRoute from "features/anonymous-pages/unsubscribe/Route";
import { linkToAnonymousWhatIsPremium } from "features/anonymous-pages/what-is-premium/links";
import AnonymousWhatIsPremium from "features/anonymous-pages/what-is-premium/Route";
import lazyWithPreload from "features/app/components/lazyWithPreload";
import { linkToUpdatePage } from "features/app/update/links";
import UpdateAppPage from "features/app/update/pages/update/components/UpdateAppPage";
import LoadableClassroom from "features/classroom/routes";
import { linkToClassroom } from "features/classroom/routes/links";
import CredentialsExpiredModal from "features/common/components/CredentialsExpiredModal";
import { PageNotFoundError } from "features/common/components/GeneralAppError/components/GeneralAppError";
import { SplashScreenForLoadableChunk } from "features/common/loadingScreens/components/SplashScreen";
import {
  browserVersionIsNotSupported,
  BrowserVersionNotSupportedModal
} from "features/common/modals/components/DeviceWarningModal";
import { CHOOSE_REGION_PATH } from "features/common/routing/consts";
import PathContainsRoute from "features/common/routing/PathContainsRoute";
import RedirectToAppropriateHomePage from "features/common/routing/RedirectToAppropriateHomePage";
import SignedInRoute from "features/common/routing/SignedInRoute";
import { linkToCreationHome } from "features/course-creation/links";
// This unused import improves the code split NavBar load time
import "features/dashboard/features/dashboard-navbar";
import {
  linkToDashboard,
  linkToJoinClass as linkToJoinClassShortened
} from "features/dashboard/links";
import { linkToStudentJoinClass } from "features/dashboard/pages/classes/links";
import DashboardRoute from "features/dashboard/routing/DashboardRoute";
import { linkToParentDashboard } from "features/parents/links";
import LoadableParentPlatform from "features/parents/routes";
import { useIsPlurallApp } from "features/plurall";
import { linkToPageNotFoundError } from "features/plurall/links";
import { linkToMobileAuthRoute } from "features/pwa/mobile/auth/links";
import { isNativeAppiOS } from "features/pwa/mobile/ios/utils/isNativeAppiOS";
import RegionSelectionModal from "features/regions/common/components/RegionSelectionModal";
import {
  linkToPasswordReset,
  linkToSignIn,
  linkToSignUp,
  linkToSignUpInvite,
  linkToSignUpParent
} from "features/sign-up/links";
import { linkToGetPremium } from "features/subscriptions/routes/get-premium/links";
import "features/teachers/features/teacher-navbar";
import { linkToTeacherDashboard } from "features/teachers/links";
import LoadableTeacherPlatform from "features/teachers/routes";
import { TutoringActivationJourneyRoutes } from "features/tutoring/activation-journey";
import { getShowPlurallHelpCentreModalOpen } from "features/ui-state/selectors";

import {
  linkToResourceDiscover,
  linkToTutorLandingPage_deprecated
} from "./features/resource-discover/routes/links";

const LoadableCreation = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "creation-chunk" */
        "features/course-creation/pages/course-creation-page/components/CourseCreation"
      )
  )
);
const SignUpParentRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-invite-chunk" */ "features/sign-up/common-routes/components/SignUpParentRoute"
      )
  )
);
const LoadableDashboardRouter = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "dashboard-router-chunk" */ "features/dashboard/containers/DashboardRouter"
      )
  )
);

const LoadableFrontDoor = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "front-door-chunk" */ "features/sign-up/components/FrontDoor"
      )
  )
);
const SignUpInviteRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-invite-chunk" */
        "features/sign-up/common-routes/components/SignUpInviteRoute"
      )
  )
);

export const PreloadableGetPremiumRoute = lazyWithPreload(
  () =>
    import(
      /* webpackChunkName: "get-premium-chunk" */ "features/subscriptions/routes/get-premium/Route"
    )
);

const LoadableAnonymousCourseDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-course-discovery-chunk" */
        "features/anonymous-pages/course-discovery/Route"
      )
  )
);

const LoadableAnonymousResourcesHubDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub-chunk" */
        "features/anonymous-pages/resources-hub/Route"
      )
  )
);

const LoadablePlurallHelpCentreModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "plurall-help-centre-modal-chunk" */
        "features/plurall/components/PlurallHelpCentreModal/PlurallHelpCentreModal"
      )
  )
);

const LoadableResourceDiscover = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub" */
        "features/resource-discover/routes/Route"
      )
  )
);

const MobileAuthRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mobile-auth" */
        "features/pwa/mobile/auth/screens/Route"
      )
  )
);

export default function Routes() {
  const location = useLocation();
  const history = useHistory();
  const refreshCredentialsError = useSelector(credentialsRefreshHasErrored);
  const showPlurallHelpCentreModal = useSelector(
    getShowPlurallHelpCentreModalOpen
  );
  const isPlurallApp = useIsPlurallApp();

  useEffectOnMount(() => {
    logReferralCode(location);

    if (window.Cypress) {
      window.__routerHistory__ = history;
    }
  });

  if (browserVersionIsNotSupported()) {
    return <BrowserVersionNotSupportedModal />;
  }

  return (
    <>
      <Suspense fallback={<SplashScreenForLoadableChunk />}>
        <Route path={"/"}>
          <TutoringActivationJourneyRoutes />
        </Route>
        <Switch>
          <Route path={linkToUpdatePage()}>
            <UpdateAppPage />
          </Route>
          <Route path={linkToSignUp()} component={LoadableFrontDoor} />
          <Route path={linkToSignIn()} component={LoadableFrontDoor} />
          <Route path={linkToPasswordReset()} component={LoadableFrontDoor} />
          <Route path={linkToSignUpInvite()}>
            <SignUpInviteRoute />
          </Route>
          <Route path={linkToSignUpParent()}>
            <SignUpParentRoute />
          </Route>
          {isPlurallApp ? (
            <SignedInRoute path={linkToClassroom()}>
              <LoadableClassroom />
            </SignedInRoute>
          ) : (
            <Route path={linkToClassroom()}>
              <LoadableClassroom />
            </Route>
          )}
          <SignedInRoute path={linkToCreationHome()}>
            <LoadableCreation />
          </SignedInRoute>

          <Route path={linkToGetPremium()}>
            <PreloadableGetPremiumRoute />
          </Route>

          <DashboardRoute path={linkToDashboard()}>
            <LoadableDashboardRouter />
          </DashboardRoute>

          <SignedInRoute path={linkToTeacherDashboard()}>
            <LoadableTeacherPlatform />
          </SignedInRoute>

          <SignedInRoute path={linkToParentDashboard()}>
            <LoadableParentPlatform />
          </SignedInRoute>
          <Redirect
            from={linkToJoinClassShortened()}
            to={linkToStudentJoinClass()}
          />
          <Route path={linkToAnonymousWhatIsPremium()}>
            <AnonymousWhatIsPremium />
          </Route>
          {!isPlurallApp && (
            <Route path={linkToAnonymousCourseDiscoveryPage()}>
              <LoadableAnonymousCourseDiscovery />
            </Route>
          )}
          {!isPlurallApp && (
            <Route path={linkToAnonymousResourcesHubPage()}>
              <LoadableAnonymousResourcesHubDiscovery />
            </Route>
          )}
          <Route path={linkToAnonymousUnsubscribePage()}>
            <AnonymousUnsubscribePageRoute />
          </Route>

          {!isPlurallApp && (
            <Route path={linkToResourceDiscover()}>
              <LoadableResourceDiscover />
            </Route>
          )}
          {!isPlurallApp && (
            <Route path={linkToTutorLandingPage_deprecated()}>
              <Redirect
                to={{ ...location, pathname: linkToResourceDiscover() }}
              />
            </Route>
          )}

          {/* TODO added temporary default error page in case seneca links were missed. Remove once all links are replaced for SOMOS app */}
          {isPlurallApp && (
            <Route path={linkToPageNotFoundError()}>
              <PageNotFoundError />
            </Route>
          )}

          {isNativeAppiOS() && (
            <Route path={linkToMobileAuthRoute()}>
              <MobileAuthRoute />
            </Route>
          )}

          <RedirectToAppropriateHomePage />
        </Switch>
      </Suspense>
      {refreshCredentialsError && <CredentialsExpiredModal />}
      {isPlurallApp && showPlurallHelpCentreModal && (
        <LoadablePlurallHelpCentreModal />
      )}
      <PathContainsRoute pathContains={CHOOSE_REGION_PATH}>
        <RegionSelectionModal />
      </PathContainsRoute>
    </>
  );
}

function logReferralCode(location: any) {
  const referral = location && getReferralCodeFromLocation(location);

  referral && analyticsReferredUserLandedOnSite(referral, location.pathname);
}
